import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import PoiService from 'service/PoiService';
import { hasPoiAccess } from '../../helpers/hasPrivilege';
import CustomDropdown from 'component/CustomDropdown';
import { PageSizeOption } from 'domain/constant/PageSizeOption';

const Poi: React.FC = () => {
  const { profile } = useSelector((state: any) => state.auth);
  const token = profile?.data?.token;
  const [data, setData] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const poiService = PoiService();

  const canCreate = hasPoiAccess(profile, 'create');
  const canUpdate = hasPoiAccess(profile, 'update');
  const canDelete = hasPoiAccess(profile, 'delete');

  const fetchData = async () => {
    const token = profile?.data?.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setLoading(true);
    try {
      const res = await poiService.poiList(token, appliedSearchTerm, currentPage, pageSize);
      setData(res.data ?? []);
      setTotalPages(res.pagination?.totalPage ?? 0);
      setTotalRecords(res.pagination?.totalRecords ?? 0);
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil data POI';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, appliedSearchTerm]);

  const handleSearchClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentPage(1);
    setAppliedSearchTerm(searchTerm);
  };

  const handleDelete = async (id: string) => {
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }
    if (!window.confirm("Apakah Anda yakin ingin menghapus POI ini?")) return;
    try {
      setLoading(true);
      await poiService.poiDelete(token, id);
      window.alert('POI berhasil dihapus');
      fetchData();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Terjadi kesalahan saat menghapus POI';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="page-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="d-flex">
            <h5 style={{ flex: 1 }}>POI</h5>
            <div>
              <p>TransMore POI</p>
            </div>
          </div>
        </div>
        <div className="col-12">
          <div className="p-4" style={{ background: 'white' }}>
            <div className="d-flex mb-3 flex-nowrap gap-2 overflow-auto text-nowrap">
              <form className="d-flex" onSubmit={handleSearchClick}>
                <input
                  type="text"
                  className="form-control form-control-compact me-2"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ minWidth: 120 }}
                />
                <button type="submit" className="btn btn-primary px-4">
                  <i className="fa-solid fa-search"></i>
                </button>
              </form>
              <div className="d-flex justify-content-end" style={{ flex: 1 }}>
                {canCreate && (
                  <>
                    <Link to="/poi/0" className='float-end text-decoration-none tm-text-primary'>
                      <button className="btn btn-primary">
                        <span className="me-2">
                          Add
                        </span>
                        <i className="fa-solid fa-plus"></i>
                      </button>
                    </Link>
                  </>
                )}
              </div>
            </div>

            <table className="table table-condensed table-hover">
              <thead className="table-primary">
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Created At</th>
                  <th>Created By</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {!loading ? (
                  data.length > 0 ? (
                    data.map((item) => (
                      <tr key={item.id}>
                        <td>{item.id}</td>
                        <td>{item.name || '-'}</td>
                        <td>{item.categoryName || item.categoryId || '-'}</td>
                        <td>{item.createdAt || '-'}</td>
                        <td>{item.createdBy || '-'}</td>
                        <td>
                          <div className="d-flex">
                            {canUpdate && (
                              <Link to={`/poi/${item.id}`}>
                                <button className="border-0 btn btn-outline-warning btn-small-compact ms-2">
                                  <i className="fa-solid fa-edit"></i>
                                </button>
                              </Link>
                            )}

                            {canDelete && (
                              <button className="border-0 btn btn-outline-danger btn-small-compact ms-2" onClick={() => handleDelete(item.id)}>
                                <i className="fa-solid fa-trash"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center">
                        Tidak ada data
                      </td>
                    </tr>
                  )
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="d-flex justify-content-end align-items-center mt-3">
              <div className="d-flex align-items-center">
                <div className="me-4 d-flex align-items-center">
                  <label className="me-2">Rows per page:</label>
                  <CustomDropdown
                    value={pageSize}
                    options={PageSizeOption.options}
                    onChange={(opt) => {
                      setPageSize(Number(opt.id));
                      setCurrentPage(1);
                    }}
                    align="top"
                  />
                </div>

                <span className="me-3">
                  {`${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, totalRecords)} of ${totalRecords}`}
                </span>

                <nav>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-left"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-left"></i>
                      </button>
                    </li>

                    <li className="page-item disabled">
                      <span className="page-link" style={{ border: 0 }}>{currentPage}</span>
                    </li>

                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-right"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(totalPages)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Poi;